import { Injectable } from '@angular/core'
import { Assistance } from '../../../models/assistance'
import { BehaviorSubject, Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { coerceToArray } from '../../../services/utils'

/**
 * Service coupled with Assistances used in AssistanceListNewComponent. Allows to lock Assistances for changes and keeps
 * track of locked Assistances, in order to survive replacing of Assistance list by backend updates.
 */

@Injectable({
  providedIn: 'root',
})
export class ListAssistanceLockService {
  lockedAssistanceURIDs = new Set<string>()
  private lockedAssistancesURIDsChange$$ = new BehaviorSubject(new Set<string>())
  public lockedAssistancesURIDsChange$: Observable<Set<string>> = this.lockedAssistancesURIDsChange$$.asObservable()
  private TAG = 'ListAssistanceLockService'

  constructor() {}

  /**
   * Locks changes to assistances according to which assistance ids are currently in lockedAssistances list. Assumes all input assistances have lockChanges set to false
   */
  synchronizeLockWhenReceivingData(assistances: Assistance[]): Assistance[] {
    return assistances.map((a) => {
      if (this.shouldBeLocked(a)) a.lockChanges()
      return a
    })
  }

  synchronizeLock<T extends Assistance>(assistances: T[]) {
    return assistances.map((a) => {
      if (this.shouldBeLocked(a)) {
        a.lockChanges()
      } else {
        a.unlockChanges()
      }
      return a
    })
  }

  lock(assistance: Assistance) {
    console.log(this.TAG, '#lock', assistance)
    if (this.shouldBeLocked(assistance)) return

    assistance.lockChanges()
    this.lockedAssistanceURIDs.add(Assistance.generateURID(assistance))
    this.lockedAssistancesURIDsChange$$.next(this.lockedAssistanceURIDs)
  }

  unlock(assistance: Assistance | Assistance[]) {
    console.log(this.TAG, '#unlock', assistance)
    coerceToArray(assistance).map((a) => this.unlockOne(a))
  }

  private unlockOne(assistance: Assistance) {
    assistance.unlockChanges()
    this.lockedAssistanceURIDs.delete(Assistance.generateURID(assistance))
    this.lockedAssistancesURIDsChange$$.next(this.lockedAssistanceURIDs)
  }

  /**
   * Will remove assistance from locked list, but does not immediately unlock it. Instead it will assume to be unlocked on the next synchronization event.
   */
  markForUnlock(assistance: Assistance) {
    console.log(this.TAG, '#markForUnlock', assistance)
    this.lockedAssistanceURIDs.delete(Assistance.generateURID(assistance))
  }

  shouldBeLocked(assistance: Assistance) {
    return this.lockedAssistanceURIDs.has(Assistance.generateURID(assistance))
  }

  syncAfterLockChanges$<T extends Assistance>(assistances: T[]) {
    return this.lockedAssistancesURIDsChange$.pipe(map((_) => this.synchronizeLock(assistances)))
  }
}
